import { DrawerBody, Empty, FCC, Icon, Loading, Separator, TextControl, useDebounce } from "@dgs/core";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { travelTemplateService } from "../../../../shared/api/travelTemplateService";
import { TravelDirection, TravelSegmentType } from "../../../../shared/types/travelSegment";
import { TravelTemplateIndexResource } from "../../../../shared/types/travelTemplate";
import { SelectableTravelTemplateSegmentCard } from "./SelectableTravelTemplateSegmentCard";
import {
	TravelSegmentDrawerEmptyList,
	TravelSegmentDrawerList,
	TravelSegmentDrawerListItem,
} from "./travelSegmentDrawerComponents";
import { TravelSegmentTypeFilterBar } from "./TravelSegmentTypeFilterBar";

interface ListProps {
	direction: TravelDirection;
	onSelect: (segment: TravelTemplateIndexResource) => void;
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: ${({ theme }) => theme.spacing(4)};

	> * {
		padding-bottom: 0;
	}
`;

const TravelTemplateList: FCC<ListProps> = ({ direction, children, onSelect }) => {
	const { t } = useTranslation();
	const [filterByType, setFilterByType] = useState<TravelSegmentType | null>(null);
	const [searchBy, setSearchBy] = useState("");
	const debouncedSearchBy = useDebounce(searchBy, 500);
	const { data: travelTemplates } = useQuery({
		queryKey: travelTemplateService.travelTemplateByDirectionKey(direction, {
			search: debouncedSearchBy,
			type: filterByType,
		}),
		queryFn: async () =>
			travelTemplateService
				.travelTemplateByDirection(direction, { search: debouncedSearchBy, type: filterByType })
				.then((response) => response.data.data),
	});

	return (
		<Container>
			<TravelSegmentTypeFilterBar
				activeType={filterByType}
				onChange={setFilterByType}
				availableTypes={["flight", "train", "car", "shuttle"]}
			/>
			<TextControl
				placeholder={t("Search")}
				name="search"
				value={searchBy}
				prefix={<Icon $icon="search" />}
				onChange={(e) => setSearchBy(e.target.value)}
			/>
			<Separator />
			<TravelSegmentDrawerList aria-label={t("Travel templates")}>
				{!travelTemplates && (
					<li>
						<Loading />
					</li>
				)}
				{travelTemplates && travelTemplates.length === 0 && (
					<TravelSegmentDrawerEmptyList>
						<Empty>{t("No travel templates found.")}</Empty>
					</TravelSegmentDrawerEmptyList>
				)}
				{travelTemplates &&
					travelTemplates.map((travelTemplate) => (
						<TravelSegmentDrawerListItem
							$isSelectable={!travelTemplate.hasSegmentFullContingent}
							key={travelTemplate.id}
							onClick={!travelTemplate.hasSegmentFullContingent ? () => onSelect(travelTemplate) : undefined}
						>
							<SelectableTravelTemplateSegmentCard travelTemplate={travelTemplate} />
						</TravelSegmentDrawerListItem>
					))}
				{children}
			</TravelSegmentDrawerList>
		</Container>
	);
};

interface Props {
	direction: TravelDirection;
	onSelect: (travelTemplate: TravelTemplateIndexResource) => void;
}

const WideDrawerBody = styled(DrawerBody)`
	flex: 1;
`;

export const AddTravelTemplateDrawerContent: FCC<Props> = ({ direction, onSelect, children }) => {
	return (
		<WideDrawerBody>
			<TravelTemplateList direction={direction} onSelect={onSelect}>
				{children}
			</TravelTemplateList>
		</WideDrawerBody>
	);
};
